import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { PacketDetailDrawer } from "./PacketDetailDrawer";

/** Q42 universal packet viewer.
 *
 *  Single instance of <PacketDetailDrawer /> mounted at the App
 *  root. Any descendent (action card, observation card,
 *  engagement card, dossier row, recall citation, etc.) calls
 *  `useOpenPacket()(packetId)` to bring the original source up in
 *  a slide-in drawer.
 *
 *  The provider only stores the packet id + open flag — the
 *  drawer owns its own fetch lifecycle so that opening the same
 *  packet twice doesn't re-fetch needlessly within the same
 *  session. */

interface PacketViewerState {
  packetId: string | null;
  isOpen: boolean;
}

interface PacketViewerActions {
  open(packetId: string): void;
  close(): void;
}

const PacketViewerActionsContext = createContext<PacketViewerActions | null>(null);
const PacketViewerStateContext = createContext<PacketViewerState | null>(null);

export function PacketViewerProvider({ children }: { children: ReactNode }) {
  const [packetId, setPacketId] = useState<string | null>(null);
  const [isOpen, setOpen] = useState(false);

  const open = useCallback((id: string) => {
    setPacketId(id);
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  const actions = useMemo<PacketViewerActions>(() => ({ open, close }), [open, close]);
  const state = useMemo<PacketViewerState>(() => ({ packetId, isOpen }), [packetId, isOpen]);

  return (
    <PacketViewerActionsContext.Provider value={actions}>
      <PacketViewerStateContext.Provider value={state}>
        {children}
        {isOpen && packetId && (
          <PacketDetailDrawer packetId={packetId} onClose={close} />
        )}
      </PacketViewerStateContext.Provider>
    </PacketViewerActionsContext.Provider>
  );
}

/** Returns a function `(packetId: string) => void` that opens the
 *  universal source-packet viewer drawer. No-op when the provider
 *  isn't mounted (e.g. during early boot / login screens). */
export function useOpenPacket(): (packetId: string) => void {
  const ctx = useContext(PacketViewerActionsContext);
  return useCallback(
    (id: string) => {
      if (ctx && id) ctx.open(id);
    },
    [ctx],
  );
}

/** Optional hook for components that want to know if the viewer
 *  is currently open + what id is showing (e.g. to suppress their
 *  own focus / hover behaviors). */
export function usePacketViewerState(): PacketViewerState {
  const s = useContext(PacketViewerStateContext);
  return s ?? { packetId: null, isOpen: false };
}
